import { useEffect, useRef, useCallback, useMemo } from 'react';
import embed from 'vega-embed';

/**
 * Custom hook for managing Vega-Lite chart lifecycle
 * Handles proper initialization, updates, and cleanup
 */
export const useVegaChart = (containerRef, spec, options = {}) => {
  const viewRef = useRef(null);
  const embedPromiseRef = useRef(null);
  const isRenderingRef = useRef(false);

  // Default embed options - memoized to prevent re-renders
  const defaultOptions = useMemo(() => ({
    actions: false,
    tooltip: true,
    renderer: 'svg',
    ...options
  }), [options]);

  // Cleanup function to properly destroy Vega view
  const cleanup = useCallback(() => {
    if (viewRef.current) {
      try {
        viewRef.current.finalize();
        viewRef.current = null;
      } catch (error) {
        console.error('Error finalizing Vega view:', error);
      }
    }
    
    // Cancel any pending embed promise
    if (embedPromiseRef.current) {
      embedPromiseRef.current = null;
    }
    
    isRenderingRef.current = false;
  }, []);

  // Render the chart
  const renderChart = useCallback(async () => {
    // Prevent multiple simultaneous renders
    if (isRenderingRef.current) {
      console.log('Chart render already in progress, skipping...');
      return;
    }

    if (!containerRef?.current || !spec) {
      return;
    }

    // Cleanup previous chart
    cleanup();

    isRenderingRef.current = true;

    try {
      // ENHANCED DEBUGGING: Validate chart spec before rendering
      const dataValues = spec?.data?.values || [];
      const encoding = spec?.encoding || {};
      const mark = spec?.mark;

      console.log('🎨 Rendering Vega-Lite chart:');
      console.log('   📊 Data:', {
        rowCount: dataValues.length,
        firstRow: dataValues[0],
        allFields: dataValues[0] ? Object.keys(dataValues[0]) : []
      });
      console.log('   🎯 Encoding:', {
        x: encoding.x?.field,
        y: encoding.y?.field,
        color: encoding.color?.field,
        theta: encoding.theta?.field,
        text: encoding.text?.field
      });
      console.log('   📈 Mark:', mark);

      // VALIDATION: Check if encoding fields exist in data
      if (dataValues.length > 0) {
        const dataFields = Object.keys(dataValues[0]);
        const encodingFields = [];

        if (encoding.x?.field) encodingFields.push(encoding.x.field);
        if (encoding.y?.field) encodingFields.push(encoding.y.field);
        if (encoding.color?.field) encodingFields.push(encoding.color.field);
        if (encoding.theta?.field) encodingFields.push(encoding.theta.field);
        if (encoding.text?.field) encodingFields.push(encoding.text.field);

        const missingFields = encodingFields.filter(field => !dataFields.includes(field));

        if (missingFields.length > 0) {
          console.error('❌ ENCODING MISMATCH - Fields in encoding but NOT in data:', missingFields);
          console.error('   Available fields:', dataFields);
          console.error('   This will cause an EMPTY CHART!');
        } else {
          console.log('✅ All encoding fields exist in data');
        }
      }

      // Store the embed promise so we can handle it properly
      embedPromiseRef.current = embed(containerRef.current, spec, defaultOptions);

      const result = await embedPromiseRef.current;

      // Only store the view if we haven't been cleaned up
      if (isRenderingRef.current) {
        viewRef.current = result.view;
        console.log('✅ Vega-Lite chart rendered successfully');
      }

      return result;
    } catch (error) {
      if (isRenderingRef.current) {
        console.error('❌ Vega-Lite rendering failed:', error);
        console.error('📊 Chart spec that failed:', JSON.stringify(spec, null, 2));
        
        // Try to render a fallback chart
        try {
          const fallbackSpec = {
            $schema: "https://vega.github.io/schema/vega-lite/v5.json",
            mark: "text",
            encoding: {
              text: { value: "Chart rendering failed" }
            },
            width: 400,
            height: 200
          };
          
          const fallbackResult = await embed(containerRef.current, fallbackSpec, defaultOptions);
          viewRef.current = fallbackResult.view;
        } catch (fallbackError) {
          console.error('Fallback chart also failed:', fallbackError);
        }
      }
      
      throw error;
    } finally {
      isRenderingRef.current = false;
    }
  }, [containerRef, spec, cleanup, defaultOptions]);

  // Effect to render chart when spec changes
  useEffect(() => {
    if (!spec || !containerRef?.current) {
      return;
    }

    // Small delay to ensure DOM is ready (helps with React StrictMode)
    const timeoutId = setTimeout(() => {
      renderChart();
    }, 100);

    // Cleanup function
    return () => {
      clearTimeout(timeoutId);
      cleanup();
    };
  }, [spec, renderChart, cleanup, containerRef]);

  return {
    view: viewRef.current,
    renderChart,
    cleanup
  };
};